import { supabase } from "@/lib/supabase";
import { storageService } from "@/services/storageService";
import type { FileUpload } from "@/services/storageService";

export type OrganizationContextScope = "district" | "school";
export type OrganizationContextLifecycleState =
  | "queued"
  | "extracting"
  | "privacy_review"
  | "indexing"
  | "ready"
  | "active"
  | "superseded"
  | "failed"
  | "quarantined"
  | "deleting"
  | "deleted";

export interface OrganizationContextVersion {
  id: string;
  canonicalFileId: string;
  versionNumber: number;
  lifecycleState: OrganizationContextLifecycleState;
  privacyState: "pending" | "cleared" | "quarantined";
  quarantineCategory: string | null;
  createdAt: string;
}

export interface OrganizationContextDocument {
  id: string;
  canonicalFileId: string;
  title: string;
  scope: OrganizationContextScope;
  schoolId: string | null;
  requestedInclusion: boolean;
  effectiveInclusion: boolean;
  staffSourceVisible: boolean;
  currentActiveVersionId: string | null;
  createdAt: string;
  versions: OrganizationContextVersion[];
}

export interface OrganizationContextMutationResult {
  requestId: string;
  contextDocumentId: string;
  contextVersionId: string;
  processingJobId: string;
  versionNumber: number;
  lifecycleState: OrganizationContextLifecycleState;
  requestedInclusion: boolean;
  effectiveInclusion: boolean;
}

export class OrganizationContextServiceError extends Error {
  constructor(
    message: string,
    public readonly code: "NOT_AUTHORIZED" | "INVALID_REQUEST" | "UNAVAILABLE",
  ) {
    super(message);
    this.name = "OrganizationContextServiceError";
  }
}

function toServiceError(error: { code?: string; message?: string } | null): OrganizationContextServiceError {
  if (error?.code === "42501") {
    return new OrganizationContextServiceError(
      "You do not have permission to manage this organization context.",
      "NOT_AUTHORIZED",
    );
  }
  if (error?.code === "23514") {
    return new OrganizationContextServiceError(
      "This organization-context request is no longer valid. Refresh and try again.",
      "INVALID_REQUEST",
    );
  }
  return new OrganizationContextServiceError(
    "Organization context is temporarily unavailable. Please try again.",
    "UNAVAILABLE",
  );
}

function requireMutationResult(
  data: Array<{
    request_id: string;
    context_document_id: string;
    context_version_id: string;
    processing_job_id: string;
    version_number: number;
    lifecycle_state: OrganizationContextLifecycleState;
    requested_inclusion: boolean;
    effective_inclusion: boolean;
  }> | null,
  error: { code?: string; message?: string } | null,
): OrganizationContextMutationResult {
  if (error || !data?.[0]) throw toServiceError(error);
  const result = data[0];
  return {
    requestId: result.request_id,
    contextDocumentId: result.context_document_id,
    contextVersionId: result.context_version_id,
    processingJobId: result.processing_job_id,
    versionNumber: result.version_number,
    lifecycleState: result.lifecycle_state,
    requestedInclusion: result.requested_inclusion,
    effectiveInclusion: result.effective_inclusion,
  };
}

export async function getCanonicalLibraryFiles(): Promise<FileUpload[]> {
  return storageService.getUserFiles();
}

export async function getOrganizationContextDocuments(
  scope: OrganizationContextScope,
  schoolId: string | null,
): Promise<OrganizationContextDocument[]> {
  let documentsQuery = supabase
    .from("organization_context_documents")
    .select("id, canonical_file_id, scope, school_id, requested_inclusion, staff_source_visible, current_active_version_id, created_at")
    .eq("scope", scope)
    .is("deleted_at", null)
    .order("created_at", { ascending: false });

  if (scope === "school") documentsQuery = documentsQuery.eq("school_id", schoolId ?? "");

  const { data: documents, error: documentsError } = await documentsQuery;
  if (documentsError) throw toServiceError(documentsError);
  if (!documents?.length) return [];

  const ids = documents.map((document) => document.id);
  const fileIds = documents.map((document) => document.canonical_file_id);
  const [versionsResult, filesResult] = await Promise.all([
    supabase
      .from("organization_context_versions")
      .select("id, context_document_id, canonical_file_id, version_number, lifecycle_state, privacy_state, quarantine_category, created_at")
      .in("context_document_id", ids)
      .order("version_number", { ascending: false }),
    supabase.from("file_uploads").select("id, file_name").in("id", fileIds),
  ]);

  if (versionsResult.error) throw toServiceError(versionsResult.error);
  if (filesResult.error) throw toServiceError(filesResult.error);

  const fileNames = new Map((filesResult.data ?? []).map((file) => [file.id, file.file_name]));
  const versionsByDocument = new Map<string, OrganizationContextVersion[]>();
  for (const version of versionsResult.data ?? []) {
    const versions = versionsByDocument.get(version.context_document_id) ?? [];
    versions.push({
      id: version.id,
      canonicalFileId: version.canonical_file_id,
      versionNumber: version.version_number,
      lifecycleState: version.lifecycle_state,
      privacyState: version.privacy_state,
      quarantineCategory: version.quarantine_category,
      createdAt: version.created_at,
    });
    versionsByDocument.set(version.context_document_id, versions);
  }

  return documents.map((document) => {
    const versions = versionsByDocument.get(document.id) ?? [];
    const activeVersion = versions.find((version) => version.id === document.current_active_version_id) ?? null;
    return {
      id: document.id,
      canonicalFileId: document.canonical_file_id,
      title: fileNames.get(document.canonical_file_id) ?? "Unavailable canonical file",
      scope: document.scope,
      schoolId: document.school_id,
      requestedInclusion: document.requested_inclusion,
      effectiveInclusion: Boolean(activeVersion && activeVersion.lifecycleState === "active"),
      staffSourceVisible: document.staff_source_visible,
      currentActiveVersionId: document.current_active_version_id,
      createdAt: document.created_at,
      versions,
    };
  });
}

export async function createOrganizationContextDocument(input: {
  requestId: string;
  canonicalFileId: string;
  scope: OrganizationContextScope;
  schoolId: string | null;
  requestedInclusion: boolean;
  staffSourceVisible: boolean;
}): Promise<OrganizationContextMutationResult> {
  const { data, error } = await supabase.rpc("create_organization_context_document", {
    _request_id: input.requestId,
    _canonical_file_id: input.canonicalFileId,
    _scope: input.scope,
    _school_id: input.schoolId,
    _requested_inclusion: input.requestedInclusion,
    _staff_source_visible: input.staffSourceVisible,
  });
  return requireMutationResult(data, error);
}

export async function createOrganizationContextReplacement(input: {
  requestId: string;
  contextDocumentId: string;
  canonicalFileId: string;
}): Promise<OrganizationContextMutationResult> {
  const { data, error } = await supabase.rpc("create_organization_context_replacement", {
    _request_id: input.requestId,
    _context_document_id: input.contextDocumentId,
    _canonical_file_id: input.canonicalFileId,
  });
  return requireMutationResult(data, error);
}

export async function requestOrganizationContextDeletion(input: {
  requestId: string;
  contextDocumentId: string;
}): Promise<void> {
  const { error } = await supabase.rpc("request_organization_context_deletion", {
    _request_id: input.requestId,
    _context_document_id: input.contextDocumentId,
  });
  if (error) throw toServiceError(error);
}

export async function setOrganizationContextInclusion(input: {
  contextDocumentId: string;
  requestedInclusion: boolean;
  staffSourceVisible: boolean;
}): Promise<OrganizationContextMutationResult | null> {
  const { data, error } = await supabase.rpc("set_organization_context_inclusion", {
    _context_document_id: input.contextDocumentId,
    _requested_inclusion: input.requestedInclusion,
    _staff_source_visible: input.staffSourceVisible,
  });
  if (error) throw toServiceError(error);
  // The RPC reports a row only when a durable candidate exists: the
  // HEY-212 re-enable paths (restore, in-flight reuse, fresh fallback
  // ingest) and the already-active on-toggle. Off (and the off-like
  // null rows) return null — there is no ingest record to surface.
  const result = data?.[0];
  if (!result) return null;
  return {
    requestId: result.request_id ?? "",
    contextDocumentId: result.context_document_id,
    contextVersionId: result.context_version_id,
    processingJobId: result.processing_job_id,
    versionNumber: result.version_number,
    lifecycleState: result.lifecycle_state,
    requestedInclusion: result.requested_inclusion,
    effectiveInclusion: result.effective_inclusion,
  };
}
